import React, { useState, useEffect } from 'react';
import { getMaterialIconForLevel } from './LuiBanner';

export type ILuiToastMessageLevels = 'success' | 'info' | 'warning' | 'error';

interface ILuiToastMessageProps {
  /** "success" | "info" | "warning" | "error" */
  level: ILuiToastMessageLevels;
  /** 0 means it will not disolve @default 4000 */
  displayTimeout?: number;
  /** allows the consumer to control when Toast fades @default true */
  display?: boolean;
  /** adds an X in the corner @default false */
  requireDismiss?: boolean;
  /** called when all said and done, allows the consumer to remove from React Dom */
  onClose?: () => void;
  children?: React.ReactNode;
}

export const LuiToastMessage: React.FC<ILuiToastMessageProps> = ({
  level,
  displayTimeout = 4000, // copied from $toast-auto-transition-animation-time
  display = true,
  requireDismiss,
  onClose,
  children,
}: ILuiToastMessageProps) => {
  const [className, setClassname] = useState('lui-msg-toast-show');
  const [fadeOutTimer, setFadeOutTimer] = useState<number | undefined>(
    undefined
  );
  const [removeTimer, setRemoveTimer] = useState<number | undefined>(undefined);

  let materialIcon = getMaterialIconForLevel(level);

  const callOnCloseAfterDissolved = () => {
    // This is so the caller can remove it from the dom
    if (onClose) {
      const timer = window.setTimeout(onClose, displayTimeout);
      setRemoveTimer(timer);
    }
  };

  useEffect(() => {
    // clear timers if previously set
    fadeOutTimer && clearTimeout(fadeOutTimer);
    removeTimer && clearTimeout(removeTimer);
    setClassname('lui-msg-toast-show');

    if (!requireDismiss && displayTimeout !== 0) {
      // set a new timer
      const timer = window.setTimeout(() => {
        setClassname('lui-msg-toast-hide');
        callOnCloseAfterDissolved();
      }, displayTimeout);
      setFadeOutTimer(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireDismiss, displayTimeout, children]);

  useEffect(() => {
    if (!display) {
      setClassname('lui-msg-toast-hide');
      callOnCloseAfterDissolved();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display]);

  return (
    <div
      role="status"
      data-testid={className}
      className={
        requireDismiss
          ? `lui-msg-${level} lui-msg-toast lui-msg-toast-dismissable ${className}`
          : `lui-msg-${level} lui-msg-toast ${className}`
      }
    >
      <i className="lui-msg-status-icon material-icons-round">{materialIcon}</i>
      {children}
      {requireDismiss && (
        <button
          aria-label="Close dialog"
          onClick={() => {
            setClassname('lui-msg-toast-hide');
            callOnCloseAfterDissolved();
          }}
        >
          <i className="material-icons-round">close</i>
        </button>
      )}
    </div>
  );
};
